import { useState, useEffect, useCallback } from 'react';
import AppHeader from '@/components/AppHeader';
import {
  getTournaments,
  getMatchesByTournament,
  getRalliesBySetForDisplay,
  getTeams,
  updateRallyFull,
  addRallyToSet,
  getMatchWithDetails,
  updateSet,
  updateMatchStatus,
  getTournament,
} from '@/lib/supabase';
import { recomputeMatchState, type DbServingState, type MatchStateInput } from '@/lib/matchState';
import { VOLLEYBALL_CATEGORY_LABELS, LOST_REASON_LABELS, VolleyballCategory, Tournament, type SetRecord, type Rally, type LostReason, type TournamentRules, type Match, type Team, type Player as TypedPlayer } from '@/types';

const CATEGORIES = Object.entries(VOLLEYBALL_CATEGORY_LABELS) as [VolleyballCategory, string][];

interface Player {
  id: string;
  name: string;
  teamId: string;
}

interface TeamInfo {
  id: string;
  name: string;
  shortName: string;
  color: string;
  players: Player[];
}

interface RallyRow {
  id: string;
  rally_number: number;
  serving_team_id: string;
  server_id: string;
  point_won_by_id: string;
  categories: { playerId: string; category: string }[];
  is_server_fault: boolean;
  lost_reason: string | null;
}

interface SetRow {
  id: string;
  set_number: number;
  score_a: number;
  score_b: number;
  ended_at: string | null;
}

interface MatchRow {
  id: string;
  match_number: number;
  status: string;
  date: string | null;
  team_a_id: string;
  team_b_id: string;
  sets: SetRow[];
}

interface CategoryDraft {
  playerId: string;
  category: VolleyballCategory;
}

interface RallyEditDraft {
  pointWonByTeamId: string;
  serverId: string;
  categories: CategoryDraft[];
  isServerFault: boolean;
  lostReason: LostReason | null;
}

interface Props {
  onSignOut: () => void;
}

export default function RallyEditor({ onSignOut }: Props) {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [sets, setSets] = useState<SetRow[]>([]);
  const [rallies, setRallies] = useState<RallyRow[]>([]);
  const [teamA, setTeamA] = useState<TeamInfo | null>(null);
  const [teamB, setTeamB] = useState<TeamInfo | null>(null);
  const [allTeamsMap, setAllTeamsMap] = useState<Map<string, { shortName: string; color: string }>>(new Map());

  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [selectedSetId, setSelectedSetId] = useState('');

  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingRallies, setLoadingRallies] = useState(false);

  // Per-rally edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RallyEditDraft>({ pointWonByTeamId: '', serverId: '', categories: [], isServerFault: false, lostReason: null });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  // Add Rally state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addDraft, setAddDraft] = useState<RallyEditDraft>({ pointWonByTeamId: '', serverId: '', categories: [], isServerFault: false, lostReason: null });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Load tournaments + all teams on mount
  useEffect(() => {
    getTournaments()
      .then(setTournaments)
      .catch(() => setTournaments([]));
    getTeams().then(all => {
      const map = new Map<string, { shortName: string; color: string }>();
      all.forEach((t: any) => map.set(t.id, { shortName: t.short_name, color: t.color }));
      setAllTeamsMap(map);
    }).catch(() => {});
  }, []);

  // Load matches when tournament changes
  useEffect(() => {
    if (!selectedTournamentId) {
      setMatches([]);
      setSelectedMatchId('');
      return;
    }
    setLoadingMatches(true);
    getMatchesByTournament(selectedTournamentId)
      .then(data => setMatches(data as MatchRow[]))
      .catch(() => setMatches([]))
      .finally(() => setLoadingMatches(false));
  }, [selectedTournamentId]);

  // Load sets + teams when match changes
  useEffect(() => {
    if (!selectedMatchId) {
      setSets([]);
      setSelectedSetId('');
      setTeamA(null);
      setTeamB(null);
      return;
    }
    const match = matches.find(m => m.id === selectedMatchId);
    if (!match) return;

    setSets(match.sets ?? []);
    setSelectedSetId('');

    // Load both teams' players
    getTeams().then(allTeams => {
      const a = allTeams.find(t => t.id === match.team_a_id);
      const b = allTeams.find(t => t.id === match.team_b_id);
      setTeamA(a ? {
        id: a.id, name: a.name, shortName: a.short_name, color: a.color,
        players: a.players.map(p => ({ id: p.id, name: p.name, teamId: p.team_id })),
      } : null);
      setTeamB(b ? {
        id: b.id, name: b.name, shortName: b.short_name, color: b.color,
        players: b.players.map(p => ({ id: p.id, name: p.name, teamId: p.team_id })),
      } : null);
    }).catch(() => {});
  }, [selectedMatchId, matches]);

  // Load rallies when set changes
  useEffect(() => {
    if (!selectedSetId) { setRallies([]); return; }
    setLoadingRallies(true);
    getRalliesBySetForDisplay(selectedSetId)
      .then(data => setRallies(data as RallyRow[]))
      .catch(() => setRallies([]))
      .finally(() => setLoadingRallies(false));
  }, [selectedSetId]);

  const allPlayers: Player[] = [...(teamA?.players ?? []), ...(teamB?.players ?? [])];

  const teamOf = useCallback((teamId: string) =>
    teamA?.id === teamId ? teamA : teamB, [teamA, teamB]);

  const playerName = useCallback((playerId: string) =>
    allPlayers.find(p => p.id === playerId)?.name ?? '?', [allPlayers]);

  const openEdit = (rally: RallyRow) => {
    setEditingId(rally.id);
    setSaveError(null);
    setSavedId(null);
    setDraft({
      pointWonByTeamId: rally.point_won_by_id,
      serverId: rally.server_id,
      isServerFault: rally.is_server_fault ?? false,
      lostReason: rally.lost_reason as LostReason | null,
      categories: (rally.categories ?? []).map(c => ({
        playerId: c.playerId,
        category: c.category as VolleyballCategory,
      })),
    });
  };

  const cancelEdit = () => { setEditingId(null); setDraft({ pointWonByTeamId: '', serverId: '', categories: [], isServerFault: false, lostReason: null }); setSaveError(null); };

  const addDraftRow = (category: VolleyballCategory = 'A') =>
    setDraft(d => ({ ...d, categories: [...d.categories, { playerId: '', category }] }));

  const updateDraftCategory = (idx: number, field: 'playerId' | 'category', value: string) => {
    setDraft(d => ({
      ...d,
      categories: d.categories.map((row, i) => i === idx ? { ...row, [field]: value } : row),
    }));
  };

  const removeDraftRow = (idx: number) =>
    setDraft(d => ({ ...d, categories: d.categories.filter((_, i) => i !== idx) }));

  // Full recompute: fetch fresh DB data, run the engine, persist set winner + match status,
  // then update local set state. Replaces the old score-only recomputeSetScoresFromRallies.
  const runFullRecompute = useCallback(async (): Promise<{ scoreA: number; scoreB: number; endedAt: string | null }> => {
    if (!selectedMatchId || !teamA || !teamB) throw new Error('Match or teams not loaded');
    const match = matches.find(m => m.id === selectedMatchId);
    if (!match) throw new Error('Match not found in local state');

    const freshData = await getMatchWithDetails(selectedMatchId);

    // Fetch tournament rules if applicable
    let tournamentRules: TournamentRules | null = null;
    if (freshData.tournament_id) {
      try {
        const t = await getTournament(freshData.tournament_id);
        tournamentRules = {
          leagueWinningPoint: t.leagueWinningPoint,
          leagueGoldenPoint: t.leagueGoldenPoint,
          playoffWinningPoint: t.playoffWinningPoint,
          playoffGoldenPoint: t.playoffGoldenPoint,
        };
      } catch { /* non-fatal */ }
    }

    const freshSets: SetRecord[] = (freshData.sets || []).map((s: any) => ({
      id: s.id,
      matchId: s.match_id,
      setNumber: s.set_number,
      scoreA: s.score_a,
      scoreB: s.score_b,
      winnerId: s.winner_id,
      startedAt: s.started_at,
      endedAt: s.ended_at,
      sideChangeShown: s.side_change_shown || false,
      rallies: (s.rallies || []).map((r: any): Rally => ({
        id: r.id,
        setId: r.set_id,
        rallyNumber: r.rally_number,
        servingTeamId: r.serving_team_id,
        serverId: r.server_id,
        pointWonById: r.point_won_by_id,
        lostReason: r.lost_reason as LostReason | null,
        pointWinnerId: r.point_winner_id,
        recordedAt: r.recorded_at,
        categories: r.categories || [],
        isServerFault: r.is_server_fault || false,
      })).sort((a: Rally, b: Rally) => b.rallyNumber - a.rallyNumber),
    }));

    const currentSetRecord = freshSets.find(s => s.endedAt === null) ?? freshSets[freshSets.length - 1];
    const currentSetRallies = currentSetRecord?.rallies ?? [];
    const dbServingState: DbServingState = {
      servingTeamId: (currentSetRecord as any)?.current_serving_team_id ?? null,
      serverId: (currentSetRecord as any)?.current_server_id ?? null,
    };

    // Build typed Team objects — player `number` isn't used by the engine, use 0
    const buildTeam = (info: TeamInfo): Team => ({
      id: info.id,
      name: info.name,
      shortName: info.shortName,
      color: info.color,
      players: info.players.map((p): TypedPlayer => ({ id: p.id, name: p.name, number: 0, teamId: p.teamId })),
    });

    const formattedMatch: Match = {
      id: freshData.id,
      matchNumber: freshData.match_number,
      date: freshData.date,
      matchTime: freshData.match_time ?? null,
      matchType: freshData.match_type as Match['matchType'],
      teamAId: freshData.team_a_id,
      teamBId: freshData.team_b_id,
      tossWonById: freshData.toss_won_by_id ?? null,
      tossChoice: freshData.toss_choice as Match['tossChoice'] ?? null,
      status: freshData.status as Match['status'],
      winnerId: freshData.winner_id,
      tournamentId: freshData.tournament_id,
      winningPoint: freshData.winning_point,
      goldenPoint: freshData.golden_point,
      startTime: freshData.start_time ?? null,
      endTime: freshData.end_time ?? null,
      createdAt: freshData.created_at,
      updatedAt: freshData.updated_at,
    };

    const recomputeInput: MatchStateInput = {
      match: formattedMatch,
      teamA: buildTeam(teamA),
      teamB: buildTeam(teamB),
      sets: freshSets,
      currentSetRallies,
      tournamentRules,
      timeouts: [],
      dbServingState,
      activeTimeout: null,
    };

    const computed = recomputeMatchState(recomputeInput);

    // Persist set corrections (score + winner + ended_at)
    if (currentSetRecord && computed.currentSet) {
      const needsSetUpdate =
        currentSetRecord.scoreA !== computed.currentSet.scoreA ||
        currentSetRecord.scoreB !== computed.currentSet.scoreB ||
        currentSetRecord.winnerId !== computed.currentSet.setWinnerId ||
        (computed.currentSet.setWinnerId && !currentSetRecord.endedAt);
      if (needsSetUpdate) {
        await updateSet(currentSetRecord.id, {
          scoreA: computed.currentSet.scoreA,
          scoreB: computed.currentSet.scoreB,
          winnerId: computed.currentSet.setWinnerId,
          endedAt: computed.currentSet.endedAt,
        });
      }
    }

    // Persist match status corrections
    const needsMatchUpdate =
      freshData.status !== computed.status ||
      freshData.winner_id !== computed.matchWinnerId ||
      (computed.status === 'match-won' && !freshData.end_time);
    if (needsMatchUpdate) {
      await updateMatchStatus(
        selectedMatchId,
        computed.status,
        computed.matchWinnerId ?? undefined,
        computed.status === 'match-won' ? new Date().toISOString() : undefined,
      );
    }

    return {
      scoreA: computed.currentSet?.scoreA ?? 0,
      scoreB: computed.currentSet?.scoreB ?? 0,
      endedAt: computed.currentSet?.endedAt ?? null,
    };
  }, [selectedMatchId, teamA, teamB, matches]);

  const handleSave = async (rally: RallyRow) => {
    if (!draft.pointWonByTeamId || !draft.serverId) {
      setSaveError('Winner and server are required.');
      return;
    }
    const match = matches.find(m => m.id === selectedMatchId);
    if (!match) return;

    // Infer serving team from server's team membership
    const serverPlayer = allPlayers.find(p => p.id === draft.serverId);
    const servingTeamId = serverPlayer?.teamId ?? rally.serving_team_id;

    setSaving(true);
    setSaveError(null);
    try {
      await updateRallyFull(rally.id, {
        pointWonById: draft.pointWonByTeamId,
        servingTeamId,
        serverId: draft.serverId,
        categories: draft.categories.filter(c => c.playerId),
        isServerFault: draft.isServerFault,
      });

      // Full recompute: updates score, winner_id, ended_at, and match status
      const updated = await runFullRecompute();

      // Update local rally state
      setRallies(prev => prev.map(r =>
        r.id === rally.id
          ? { ...r, point_won_by_id: draft.pointWonByTeamId, server_id: draft.serverId, serving_team_id: servingTeamId, categories: draft.categories.filter(c => c.playerId), is_server_fault: draft.isServerFault }
          : r
      ));
      // Update local set: score + ended_at (reflects set winner)
      setSets(prev => prev.map(s =>
        s.id === selectedSetId ? { ...s, score_a: updated.scoreA, score_b: updated.scoreB, ended_at: updated.endedAt } : s
      ));

      setSavedId(rally.id);
      setEditingId(null);
      setDraft({ pointWonByTeamId: '', serverId: '', categories: [], isServerFault: false, lostReason: null });
    } catch (err: any) {
      setSaveError(err?.message ?? (typeof err === 'string' ? err : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddRally = async () => {
    if (!addDraft.pointWonByTeamId || !addDraft.serverId) {
      setAddError('Winner and server are required.');
      return;
    }
    const match = matches.find(m => m.id === selectedMatchId);
    if (!match) return;
    const serverPlayer = allPlayers.find(p => p.id === addDraft.serverId);
    const servingTeamId = serverPlayer?.teamId ?? addDraft.pointWonByTeamId;

    setAddSaving(true);
    setAddError(null);
    try {
      const newRally = await addRallyToSet({
        setId: selectedSetId,
        servingTeamId,
        serverId: addDraft.serverId,
        pointWonById: addDraft.pointWonByTeamId,
        categories: addDraft.categories.filter(c => c.playerId),
        isServerFault: addDraft.isServerFault,
        lostReason: addDraft.lostReason ?? null,
      });
      // Full recompute: updates score, winner_id, ended_at, and match status
      const updated = await runFullRecompute();
      // Append new rally to local list
      setRallies(prev => [...prev, {
        id: newRally.id,
        rally_number: newRally.rally_number,
        serving_team_id: servingTeamId,
        server_id: addDraft.serverId,
        point_won_by_id: addDraft.pointWonByTeamId,
        categories: addDraft.categories.filter(c => c.playerId),
        is_server_fault: addDraft.isServerFault,
        lost_reason: null,
      }]);
      setSets(prev => prev.map(s =>
        s.id === selectedSetId ? { ...s, score_a: updated.scoreA, score_b: updated.scoreB, ended_at: updated.endedAt } : s
      ));
      setShowAddForm(false);
      setAddDraft({ pointWonByTeamId: '', serverId: '', categories: [], isServerFault: false, lostReason: null });
    } catch (err: any) {
      setAddError(err?.message ?? (typeof err === 'string' ? err : 'Failed to add rally'));
    } finally {
      setAddSaving(false);
    }
  };

  // Compute running score for display (ascending by rally_number)
  const runningScores = (() => {
    let a = 0, b = 0;
    return rallies.map(r => {
      if (r.point_won_by_id === teamA?.id) a++;
      else if (r.point_won_by_id === teamB?.id) b++;
      return { a, b };
    });
  })();

  const selectedSet = sets.find(s => s.id === selectedSetId);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader onSignOut={onSignOut} title="Rally Category Editor" navIcon="admin" />

      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-2xl mx-auto w-full">
        {/* Selectors */}
        <div className="card mb-4 space-y-3">
          <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider">Select Match</h2>

          {/* Tournament */}
          <div>
            <label className="text-xs text-text-muted mb-1 block">Tournament</label>
            <select
              value={selectedTournamentId}
              onChange={e => { setSelectedTournamentId(e.target.value); setSelectedMatchId(''); setSelectedSetId(''); }}
              className="input-field text-sm"
            >
              <option value="">— pick a tournament —</option>
              {tournaments.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Match */}
          {selectedTournamentId && (
            <div>
              <label className="text-xs text-text-muted mb-1 block">Match</label>
              {loadingMatches ? (
                <p className="text-xs text-text-muted">Loading matches…</p>
              ) : (
                <select
                  value={selectedMatchId}
                  onChange={e => { setSelectedMatchId(e.target.value); setSelectedSetId(''); }}
                  className="input-field text-sm"
                >
                  <option value="">— pick a match —</option>
                  {matches.map(m => {
                    const a = allTeamsMap.get(m.team_a_id);
                    const b = allTeamsMap.get(m.team_b_id);
                    const dateStr = m.date
                      ? new Date(m.date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                      : '';
                    const vs = a && b ? `${a.shortName} vs ${b.shortName}` : '';
                    return (
                      <option key={m.id} value={m.id}>
                        #{m.match_number}{vs ? ` — ${vs}` : ''}{dateStr ? ` · ${dateStr}` : ''} ({m.status})
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          )}

          {/* Set */}
          {selectedMatchId && teamA && teamB && (
            <div>
              <label className="text-xs text-text-muted mb-1 block">Set</label>
              <select
                value={selectedSetId}
                onChange={e => setSelectedSetId(e.target.value)}
                className="input-field text-sm"
              >
                <option value="">— pick a set —</option>
                {sets.map(s => (
                  <option key={s.id} value={s.id}>
                    Set {s.set_number}
                    {s.ended_at ? ` (${s.score_a}–${s.score_b})` : ' — in progress'}
                  </option>
                ))}
              </select>
              <div className="flex gap-3 mt-1.5">
                <span className="text-xs font-semibold" style={{ color: teamA.color }}>{teamA.shortName}</span>
                <span className="text-xs text-text-muted">vs</span>
                <span className="text-xs font-semibold" style={{ color: teamB.color }}>{teamB.shortName}</span>
              </div>
            </div>
          )}
        </div>

        {/* Rallies */}
        {selectedSetId && (
          <>
            {loadingRallies ? (
              <div className="text-center py-10 text-text-muted text-sm">Loading rallies…</div>
            ) : rallies.length === 0 ? (
              <div className="text-center py-10 text-text-muted text-sm">No rallies found for this set.</div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider">
                    {rallies.length} Rallies — Set {selectedSet?.set_number}
                  </h2>
                  {savedId && (
                    <span className="text-xs text-success font-semibold">✓ Saved</span>
                  )}
                </div>

                {rallies.map((rally, idx) => {
                  const servingTeam = teamOf(rally.serving_team_id);
                  const winnerTeam = teamOf(rally.point_won_by_id);
                  const score = runningScores[idx];
                  const isEditing = editingId === rally.id;
                  const justSaved = savedId === rally.id && !isEditing;

                  return (
                    <div
                      key={rally.id}
                      className={`rounded-lg border transition-all ${
                        isEditing
                          ? 'border-primary bg-primary/5'
                          : justSaved
                          ? 'border-success/50 bg-success/5'
                          : 'border-border bg-surface'
                      }`}
                    >
                      {/* Rally summary row */}
                      <div className="px-3 py-2.5 flex items-center gap-2">
                        {/* Rally number + score */}
                        <span className="text-xs text-text-muted font-mono w-6 flex-shrink-0">
                          #{rally.rally_number}
                        </span>
                        <span className="font-mono-nums text-xs text-text-secondary flex-shrink-0 w-10 text-center">
                          {score.a}–{score.b}
                        </span>

                        {/* Server info */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span
                            className="text-xs font-bold"
                            style={{ color: servingTeam?.color }}
                          >
                            {servingTeam?.shortName}
                          </span>
                          <span className="text-xs text-text-muted truncate max-w-[80px]">
                            {playerName(rally.server_id)}
                          </span>
                        </div>

                        {/* Arrow + winner */}
                        <span className="text-text-muted text-xs flex-shrink-0">→</span>
                        <span
                          className="text-xs font-bold flex-shrink-0"
                          style={{ color: winnerTeam?.color }}
                        >
                          {winnerTeam?.shortName}
                        </span>

                        {/* Fault badges */}
                        {rally.is_server_fault && (
                          <span className="text-[10px] bg-error/20 text-error px-1.5 py-0.5 rounded font-bold flex-shrink-0">FAULT</span>
                        )}
                        {rally.lost_reason === 'FOUL' && (
                          <span className="text-[10px] bg-warning/20 text-warning px-1.5 py-0.5 rounded font-bold flex-shrink-0">FOUL</span>
                        )}

                        <div className="flex-1 min-w-0" />

                        {/* Edit button */}
                        {!isEditing && (
                          <button
                            onClick={() => openEdit(rally)}
                            className="text-sm text-primary hover:text-primary/80 font-bold flex-shrink-0 py-1.5 px-4 rounded-lg border border-primary/40 hover:border-primary active:scale-95 transition-all"
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      {/* Current categories (read mode) */}
                      {!isEditing && (
                        <div className="px-3 pb-2 flex flex-wrap gap-1">
                          {(rally.categories ?? []).length === 0 ? (
                            <span className="text-[10px] text-text-muted italic">No categories</span>
                          ) : (
                            (rally.categories).map((c, ci) => {
                              const player = allPlayers.find(p => p.id === c.playerId);
                              const pTeam = player ? teamOf(player.teamId) : null;
                              return (
                                <span
                                  key={ci}
                                  className="text-[10px] px-1.5 py-0.5 rounded font-semibold border"
                                  style={{ color: pTeam?.color, borderColor: `${pTeam?.color}50` }}
                                >
                                  {c.category}: {player?.name?.split(' ')[0] ?? '?'}
                                </span>
                              );
                            })
                          )}
                        </div>
                      )}

                      {/* Inline editor */}
                      {isEditing && (
                        <div className="px-3 pb-4 pt-2 space-y-4 border-t border-primary/20">

                          {/* ── Winner ── */}
                          <div>
                            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Point won by</p>
                            <div className="grid grid-cols-2 gap-2">
                              {[teamA, teamB].filter(Boolean).map(t => (
                                <button
                                  key={t!.id}
                                  onClick={() => setDraft(d => ({ ...d, pointWonByTeamId: t!.id }))}
                                  className={`py-3 px-3 rounded-lg font-bold text-sm transition-all active:scale-95 border-2 ${
                                    draft.pointWonByTeamId === t!.id
                                      ? 'text-white'
                                      : 'bg-surface-raised text-text-primary border-border'
                                  }`}
                                  style={draft.pointWonByTeamId === t!.id ? { backgroundColor: t!.color, borderColor: t!.color } : {}}
                                >
                                  {t!.shortName}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* ── Server ── */}
                          <div>
                            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Server</p>
                            <select
                              value={draft.serverId}
                              onChange={e => setDraft(d => ({ ...d, serverId: e.target.value }))}
                              className="w-full text-sm py-2.5 px-3 rounded-lg bg-surface-raised border border-border text-text-primary focus:outline-none focus:border-primary"
                            >
                              <option value="">— select server —</option>
                              {teamA && (
                                <optgroup label={teamA.name}>
                                  {teamA.players.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))}
                                </optgroup>
                              )}
                              {teamB && (
                                <optgroup label={teamB.name}>
                                  {teamB.players.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          </div>

                          {/* ── Categories ── */}
                          <div>
                            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Categories</p>
                            {draft.categories.length === 0 && (
                              <p className="text-xs text-text-muted italic mb-2">No categories assigned.</p>
                            )}
                            <div className="space-y-2">
                              {draft.categories.map((row, di) => (
                                <div key={di} className="flex items-center gap-2">
                                  <select
                                    value={row.category}
                                    onChange={e => updateDraftCategory(di, 'category', e.target.value)}
                                    className="text-sm py-2.5 px-2 rounded-lg bg-surface-raised border border-border text-primary font-bold focus:outline-none w-20 flex-shrink-0"
                                  >
                                    {CATEGORIES.map(([key, label]) => (
                                      <option key={key} value={key}>{key} — {label}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={row.playerId}
                                    onChange={e => updateDraftCategory(di, 'playerId', e.target.value)}
                                    className="flex-1 min-w-0 text-sm py-2.5 px-3 rounded-lg bg-surface-raised border border-border text-text-primary focus:outline-none focus:border-primary"
                                  >
                                    <option value="">— select player —</option>
                                    {teamA && (
                                      <optgroup label={teamA.name}>
                                        {teamA.players.map(p => (
                                          <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {teamB && (
                                      <optgroup label={teamB.name}>
                                        {teamB.players.map(p => (
                                          <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                  </select>
                                  <button
                                    onClick={() => removeDraftRow(di)}
                                    className="p-2.5 rounded-lg text-error hover:bg-error/10 active:scale-95 transition-all flex-shrink-0"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                      <path d="M18 6 6 18M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              {CATEGORIES.map(([key, label]) => (
                                <button
                                  key={key}
                                  onClick={() => addDraftRow(key)}
                                  className="py-2.5 px-2 rounded-lg border border-border bg-surface-raised hover:border-primary hover:bg-primary/5 active:scale-95 transition-all text-center"
                                >
                                  <span className="block text-sm font-bold text-text-primary">{key}</span>
                                  <span className="block text-[10px] text-text-muted leading-tight">{label}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Server Fault toggle */}
                          <label className="flex items-center gap-3 cursor-pointer select-none">
                            <div
                              onClick={() => setDraft(d => ({ ...d, isServerFault: !d.isServerFault }))}
                              className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 ${draft.isServerFault ? 'bg-error' : 'bg-surface-raised border border-border'}`}
                            >
                              <div className="w-5 h-5 rounded-full bg-white shadow transition-transform mt-0.5" style={{ transform: `translateX(${draft.isServerFault ? 18 : 2}px)` }} />
                            </div>
                            <span className={`text-sm font-semibold ${draft.isServerFault ? 'text-error' : 'text-text-muted'}`}>
                              Server Fault
                            </span>
                          </label>

                          {saveError && (
                            <p className="text-sm text-error font-semibold">{saveError}</p>
                          )}

                          <div className="flex gap-2 pt-1">
                            <button
                              onClick={() => handleSave(rally)}
                              disabled={saving}
                              className="flex-1 py-3 rounded-lg font-bold text-sm bg-primary text-white hover:opacity-90 disabled:opacity-50 active:scale-[0.98] transition-all"
                            >
                              {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={saving}
                              className="py-3 px-5 rounded-lg font-semibold text-sm border border-border text-text-secondary hover:text-text-primary hover:bg-surface-raised active:scale-[0.98] transition-all"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Add Rally ── */}
            {teamA && teamB && (
              <div className="mt-4">
                {!showAddForm ? (
                  <button
                    onClick={() => { setShowAddForm(true); setAddError(null); }}
                    className="w-full py-3 rounded-lg border-2 border-dashed border-border text-text-muted hover:border-primary hover:text-primary font-semibold text-sm transition-all active:scale-[0.98]"
                  >
                    + Add Rally
                  </button>
                ) : (
                  <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4 space-y-4">
                    <p className="text-xs font-bold text-primary uppercase tracking-wider">Add Rally #{(rallies[rallies.length - 1]?.rally_number ?? 0) + 1}</p>

                    {/* Winner */}
                    <div>
                      <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Point won by</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[teamA, teamB].map(t => (
                          <button
                            key={t.id}
                            onClick={() => setAddDraft(d => ({ ...d, pointWonByTeamId: t.id }))}
                            className={`py-3 px-3 rounded-lg font-bold text-sm transition-all active:scale-95 border-2 ${addDraft.pointWonByTeamId === t.id ? 'text-white' : 'bg-surface-raised text-text-primary border-border'}`}
                            style={addDraft.pointWonByTeamId === t.id ? { backgroundColor: t.color, borderColor: t.color } : {}}
                          >
                            {t.shortName}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Server */}
                    <div>
                      <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Server</p>
                      <select
                        value={addDraft.serverId}
                        onChange={e => setAddDraft(d => ({ ...d, serverId: e.target.value }))}
                        className="w-full text-sm py-2.5 px-3 rounded-lg bg-surface-raised border border-border text-text-primary focus:outline-none focus:border-primary"
                      >
                        <option value="">— select server —</option>
                        <optgroup label={teamA.name}>
                          {teamA.players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </optgroup>
                        <optgroup label={teamB.name}>
                          {teamB.players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </optgroup>
                      </select>
                    </div>

                    {/* Categories */}
                    <div>
                      <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Categories (optional)</p>
                      <div className="space-y-2 mb-2">
                        {addDraft.categories.map((row, di) => (
                          <div key={di} className="flex items-center gap-2">
                            <select
                              value={row.category}
                              onChange={e => setAddDraft(d => ({ ...d, categories: d.categories.map((r, i) => i === di ? { ...r, category: e.target.value as VolleyballCategory } : r) }))}
                              className="text-sm py-2 px-2 rounded-lg bg-surface-raised border border-border text-primary font-bold w-20 flex-shrink-0"
                            >
                              {CATEGORIES.map(([key, label]) => <option key={key} value={key}>{key} — {label}</option>)}
                            </select>
                            <select
                              value={row.playerId}
                              onChange={e => setAddDraft(d => ({ ...d, categories: d.categories.map((r, i) => i === di ? { ...r, playerId: e.target.value } : r) }))}
                              className="flex-1 text-sm py-2 px-3 rounded-lg bg-surface-raised border border-border text-text-primary focus:outline-none"
                            >
                              <option value="">— player —</option>
                              <optgroup label={teamA.name}>{teamA.players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>
                              <optgroup label={teamB.name}>{teamB.players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>
                            </select>
                            <button onClick={() => setAddDraft(d => ({ ...d, categories: d.categories.filter((_, i) => i !== di) }))} className="p-2 rounded text-error hover:bg-error/10 flex-shrink-0">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-6 gap-1">
                        {CATEGORIES.map(([key]) => (
                          <button key={key} onClick={() => setAddDraft(d => ({ ...d, categories: [...d.categories, { playerId: '', category: key }] }))}
                            className="py-2 rounded-lg border border-border bg-surface-raised text-xs font-bold text-text-primary hover:border-primary active:scale-95 transition-all">
                            {key}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Server fault */}
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <div
                        onClick={() => setAddDraft(d => ({ ...d, isServerFault: !d.isServerFault }))}
                        className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 ${addDraft.isServerFault ? 'bg-error' : 'bg-surface-raised border border-border'}`}
                      >
                        <div className="w-5 h-5 rounded-full bg-white shadow transition-transform mt-0.5" style={{ transform: `translateX(${addDraft.isServerFault ? 18 : 2}px)` }} />
                      </div>
                      <span className={`text-sm font-semibold ${addDraft.isServerFault ? 'text-error' : 'text-text-muted'}`}>Server Fault</span>
                    </label>

                    {/* Lost reason */}
                    <div>
                      <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-2">Lost Reason (optional)</p>
                      <select
                        value={addDraft.lostReason ?? ''}
                        onChange={e => setAddDraft(d => ({ ...d, lostReason: (e.target.value || null) as LostReason | null }))}
                        className="w-full text-sm py-2.5 px-3 rounded-lg bg-surface-raised border border-border text-text-primary focus:outline-none focus:border-primary"
                      >
                        <option value="">— none —</option>
                        {(Object.entries(LOST_REASON_LABELS) as [LostReason, string][]).map(([key, label]) => (
                          <option key={key} value={key}>{key} — {label}</option>
                        ))}
                      </select>
                    </div>

                    {addError && <p className="text-sm text-error font-semibold">{addError}</p>}

                    <div className="flex gap-2">
                      <button onClick={handleAddRally} disabled={addSaving}
                        className="flex-1 py-3 rounded-lg font-bold text-sm bg-primary text-white hover:opacity-90 disabled:opacity-50 active:scale-[0.98] transition-all">
                        {addSaving ? 'Adding…' : 'Add Rally'}
                      </button>
                      <button onClick={() => { setShowAddForm(false); setAddError(null); setAddDraft({ pointWonByTeamId: '', serverId: '', categories: [], isServerFault: false, lostReason: null }); }} disabled={addSaving}
                        className="py-3 px-5 rounded-lg font-semibold text-sm border border-border text-text-secondary hover:bg-surface-raised active:scale-[0.98] transition-all">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
